// Copyright (c) Microsoft. All rights reserved.

import React, { useEffect } from 'react';
import { useChat } from '../../../../libs/hooks';
import { ModalDialog } from '../ChatWindow/Shared/ModalDialog';

interface JoinChatDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export const JoinChatDialog: React.FC<JoinChatDialogProps> = ({ open, setOpen }) => {
    const chat = useChat();
    const [errorOccurred, setErrorOccurred] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    useEffect(() => {
        setErrorOccurred(false);
        setErrorMessage('');
    }, [open]);

    const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        setErrorOccurred(false);

        const chatId = ev.currentTarget.elements.namedItem('chat-id-input') as HTMLInputElement;

        void chat.joinChat(chatId.value).then(({ success, message }) => {
            if (success) {
                setOpen(false);
            } else {
                setErrorOccurred(true);
                setErrorMessage(message);
            }
        });
    };

    return (
        <ModalDialog title="Join a shared conversation" open={open} onClose={() => setOpen(false)}>
            <form onSubmit={handleSubmit} className="w-full sm:max-w-md sm:w-[28rem]">
                <div className="mb-4">Please enter the ID of the chat you would like to join:</div>
                <input
                    type="text"
                    id="chat-id-input"
                    className="bg-gray-50 autofill:bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg outline-none focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-slate-900 dark:autofill:bg-slate-900 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 mb-4"
                />
                {errorOccurred && <div className="text-red-500">{errorMessage}</div>}
                <div className="mt-4 flex justify-end gap-4">
                    <button
                        type="button"
                        className="shadow-sm ring-1 ring-inset ring-gray-300 dark:shadow-none dark:ring-0 inline-flex items-center gap-2 rounded-md bg-white hover:bg-gray-50 dark:hover:bg-slate-400 text-gray-90 dark:bg-slate-600 py-1.5 px-3 text-sm/6 font-semibold dark:text-white data-[open]:bg-gray-700 data-[focus]:outline-1 data-[focus]:outline-white"
                        onClick={() => setOpen(false)}>
                        Cancel
                    </button>
                    <button
                        className="inline-flex w-16 justify-center items-center gap-2 rounded-md bg-primary-400 py-1.5 px-3 text-sm/6 font-semibold text-white data-[hover]:bg-primary-600 data-[open]:bg-gray-700 data-[focus]:outline-1 data-[focus]:outline-white"
                        type="submit">
                        Join
                    </button>
                </div>
            </form>
        </ModalDialog>
    );
};
